import { FC, useCallback, useState } from 'react'
import { Settings2 } from 'lucide-react'
import { useBoolean, useMount } from 'ahooks'
import { CheckedState } from '@radix-ui/react-checkbox'

import { Button, Checkbox } from '@genie-fintech/ui/components'
import { title } from '@genie-fintech/ui/style/typography'

import Drawer from '$components/Drawer'
import CloseButton from '$components/CloseButton'
import Loading from '$components/Loading'
import Empty from '$components/Empty'

import { TID } from '$services/api'
import { useAppGroupService } from '$hooks/services'

import {
  drawerContainer,
  drawerFooter,
  drawerHeader,
  drawerMain,
  grid
} from './styles.css'
import { useGroupFilter } from './useGroupFilter'

type Props = ReturnType<typeof useGroupFilter> & {
  appId?: TID
}

const DrawerContent: FC<Props & { onClose: VoidFunction }> = props => {
  const { appId, filteredGroupIds, onChangeFilteredGroupIds, onClose } = props

  const [selectedGroupIds, setSelectedGroupIds] = useState<
    Props['filteredGroupIds']
  >([])

  const { fetchGroupSelectList, fetchingGroupSelectList, groupOptions } =
    useAppGroupService()

  useMount(() => {
    setSelectedGroupIds(filteredGroupIds)
    if (!appId) return
    fetchGroupSelectList({ application_id: appId })
  })

  const onClearFilter = useCallback(() => {
    setSelectedGroupIds([])
    onChangeFilteredGroupIds([])
    onClose()
  }, [onClose, onChangeFilteredGroupIds])

  const onApply = useCallback(() => {
    onChangeFilteredGroupIds(selectedGroupIds)
    onClose()
  }, [onClose, onChangeFilteredGroupIds, selectedGroupIds])

  const isEmpty = !fetchingGroupSelectList && !groupOptions.length

  const hasData = !!groupOptions.length

  return (
    <>
      <header className={drawerHeader}>
        <p className={title.five}>Filter</p>
        <CloseButton onClick={onClose} />
      </header>
      <main className={drawerMain}>
        <p className={title.five}>Groups</p>

        {fetchingGroupSelectList && <Loading />}

        {isEmpty && <Empty title="No Group" />}

        {hasData && (
          <article className={grid}>
            {groupOptions.map(group => {
              const checked = selectedGroupIds.includes(group.value)
              const onCheckedChange = (checked: CheckedState) => {
                setSelectedGroupIds(prev => {
                  return checked
                    ? [...prev, group.value]
                    : prev.filter(id => id !== group.value)
                })
              }
              return (
                <Checkbox
                  key={group.value}
                  label={group.label.replace(/_/g, ' ')}
                  styleVariants={{ bordered: true, padded: true }}
                  boxProps={{ checked, onCheckedChange }}
                />
              )
            })}
          </article>
        )}
      </main>
      <footer className={drawerFooter}>
        <Button
          onClick={onClearFilter}
          styleVariants={{ kind: 'neutral', type: 'outlined' }}
          disabled={!selectedGroupIds.length}
        >
          Clear filter
        </Button>

        <Button onClick={onApply}>Apply</Button>
      </footer>
    </>
  )
}

const GroupFilter: FC<Props> = props => {
  const [open, { setTrue, setFalse, toggle }] = useBoolean()

  return (
    <>
      <Button
        onClick={setTrue}
        styleVariants={{ kind: 'neutral', type: 'outlined', size: 'big' }}
      >
        <Settings2 size={16} />
        Filter
      </Button>

      <Drawer open={open} onOpenChange={toggle} className={drawerContainer}>
        <DrawerContent {...props} onClose={setFalse} />
      </Drawer>
    </>
  )
}

export default GroupFilter
